/**
 * useEmailContextState — binds the per-email context lookup to the CURRENTLY
 * selected mail item, with the race guard the shared-mailbox code already
 * learned the hard way (story pinned-pane, AC-5).
 *
 * THE HAZARD, verbatim from `features/sharedMailbox/sharedMailboxTarget.ts`:
 * an ItemChanged re-detect leaves the previous message's answer live until the
 * async round-trip resolves. For a task pane the AM clicks through all day
 * that is not a cosmetic glitch — it is the previous email's ACTION offered on
 * this email.
 *
 * THE GUARD IS AN IDENTITY COMPARISON, and it is structural rather than
 * procedural: the resolved answer is stored WITH the identity it was fetched
 * for, and the render only uses it when that identity is still the current
 * one. So:
 *   - the state goes to `resolving` the moment the identity changes,
 *     SYNCHRONOUSLY, with no effect ordering to get wrong — a derived value
 *     cannot be one render late;
 *   - a late response for the PREVIOUS email is dropped, not rendered;
 *   - and re-selecting an email whose request is still in flight cannot let a
 *     stale body through, which a request-sequence counter alone would.
 *
 * ITEMCHANGED is NOT registered here. `useMailboxItem` already registers it —
 * through the runtime `Office.EventType.ItemChanged` lookup, in a try/catch,
 * removed on unmount, with no numeric event code anywhere — and re-reads the
 * item. This hook consumes that output. A second registration would be a
 * second source of truth for "which email is open".
 */

import { useEffect, useRef, useState } from "react";
import type { EmailContext as MailboxEmailContext } from "./useMailboxItem";
import type { EmailRunStateResponse } from "../types/workflow";

export interface UseEmailContextStateOptions {
  /** The selected item, from `useMailboxItem`. `null` while it is being read. */
  emailContext: MailboxEmailContext | null;
  /** The per-email lookup. Injected so the hook is testable without fetch. */
  getEmailRunState: (
    messageIdentity: string,
    mailboxAddress?: string | null,
  ) => Promise<EmailRunStateResponse>;
  /**
   * The mailbox this email was read from (story 26727) — narrows which
   * mailbox's triage record the returned `brief` reflects. `null`/omitted
   * resolves to "any mailbox for this identity" server-side.
   */
  mailboxAddress?: string | null;
  /**
   * Bump to re-read the CURRENT email. The host bumps it after a launch: the
   * launched email is usually still selected, so no ItemChanged fires and the
   * strip would keep its pre-launch answer. The previous answer for the same
   * email stays on screen until the re-read lands.
   */
  refreshSignal?: number;
  /** Skip all side effects (tests). */
  disabled?: boolean;
}

export interface UseEmailContextStateResult {
  /**
   * The DECLARED identity of the selected email, or null when there is
   * nothing to look up. Never a mailbox-scoped id.
   */
  identity: string | null;
  /**
   * True when there is nothing to resolve — no selection, a non-mail
   * selection, or a mail item whose `internetMessageId` the host did not
   * surface. NO lookup is fired in any of those cases.
   */
  noItem: boolean;
  /** The answer FOR THE CURRENT IDENTITY, or null while resolving. */
  lookup: EmailRunStateResponse | null;
  /** Set when the lookup for the current identity failed. */
  error: Error | null;
}

/**
 * The DECLARED per-email identity and nothing else.
 *
 * NO FALLBACK to `emailId` / `restId`: a mailbox-scoped id here would
 * re-create, client-side, exactly the guess this feature exists to remove. An
 * email whose `internetMessageId` the host does not surface resolves to
 * `no_item` with a plain statement instead.
 */
export function declaredEmailIdentity(
  emailContext: MailboxEmailContext | null,
): string | null {
  const value = emailContext?.internetMessageId;
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function useEmailContextState(
  options: UseEmailContextStateOptions,
): UseEmailContextStateResult {
  const {
    emailContext,
    getEmailRunState,
    mailboxAddress = null,
    refreshSignal = 0,
    disabled = false,
  } = options;

  const identity = declaredEmailIdentity(emailContext);
  // Story 26727: the answer is a function of identity AND mailbox — the same
  // email read from two mailboxes can now resolve to two different triage
  // records. A key on identity alone would let mailbox A's answer render
  // while the AM is looking at mailbox B's copy of the same message.
  const key = identity ? `${identity}|${mailboxAddress ?? ""}` : null;

  // Answers are stored WITH the key they belong to. Nothing here is
  // "the current answer" — it is "the answer to that question".
  const [resolved, setResolved] = useState<{
    key: string;
    lookup: EmailRunStateResponse | null;
    error: Error | null;
  } | null>(null);

  const getEmailRunStateRef = useRef(getEmailRunState);
  getEmailRunStateRef.current = getEmailRunState;

  useEffect(() => {
    if (disabled || !identity || !key) return;
    let cancelled = false;
    void (async () => {
      try {
        const lookup = await getEmailRunStateRef.current(identity, mailboxAddress);
        if (cancelled) return;
        setResolved({ key, lookup, error: null });
      } catch (err) {
        if (cancelled) return;
        setResolved({
          key,
          lookup: null,
          error: err instanceof Error ? err : new Error(String(err)),
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [identity, mailboxAddress, key, disabled, refreshSignal]);

  // THE GUARD. A stored answer is used only while its key is still the
  // selected one; otherwise this reads as `resolving`, with no action.
  const current = resolved && key && resolved.key === key ? resolved : null;

  return {
    identity,
    noItem: identity === null,
    lookup: current?.lookup ?? null,
    error: current?.error ?? null,
  };
}

export default useEmailContextState;
